import {
  CATCH_BEAT_MS,
  DEAL_LANDED_MS,
  DEAL_STEP_MS,
  DRAW_FLIGHT_MS,
  FAN_MS,
  FLIP_MS,
  MAX_TURNED_BACK_SHOWN,
  moveSpanMs,
  openingSpanMs,
  PAY_STEP_MS,
  playEffectAtMs,
  PLAY_FLIGHT_MS,
  PRESS_MS,
  RESHUFFLE_MS,
  ROUND_BEAT_MS,
  ROUND_END_SPAN_MS,
  ROUND_HANDS_MS,
  TAKE_FLIGHT_MS,
  TARGET_BEAT_MS,
  TURNED_BACK_RETURN_MS,
  TURNED_BACK_SHOW_MS,
  type HaftkhabisEvent,
  type HaftkhabisRoundResult,
  type HaftkhabisView,
} from "@bing/engine-haftkhabis";
import type { SfxName } from "../sound/sfx";
import { DIAMOND_SEAT_EDGES, seatEdge, type SeatEdge } from "./card-motion";
import { CLIP_LEAD_MS, cueAt, type HaftkhabisClip } from "./haftkhabis-sfx";

/*
 * EVERY EVENT AT THE هفت خبیث TABLE HAPPENS VISIBLY AND IN ITS ORDER (the operator's words): a frame arrives holding
 * the state AFTER a move, so the table never simply draws it. It draws a MOMENT: a timeline, worked out here from the
 * two frames and the move's events, pure, whose numbers are the engine's own (`pacing.ts`, which also holds the next
 * bot and the first window for exactly as long). A moment is three lists on one clock:
 *
 *   scenes   what the table draws from each instant on: the card is in the air, THEN it has landed and the count
 *            has changed, THEN its effect shows. Each scene is whole, so a test can read any instant;
 *   flights  every card in the air, each from its anchor at its time: mounted once when the moment starts;
 *   cues     every sound, at its moment less its clip's own lead-in (`haftkhabis-sfx.ts`).
 *
 * `use-haftkhabis-feed.ts` runs them; `HaftkhabisTable` draws what it is told. A frame that lands while a moment is on
 * screen waits its turn behind it (`haftkhabis-effects.ts`), so fast frames never bunch their sounds.
 */

/** Where a card in the air is: the felt's pile or discard, your dealt stack, or a seat. */
export type Anchor = "pile" | "discard" | "stack" | "take" | number;

/** One card in the air: `at` ms after the moment starts it leaves `from`, `ms` later it is at `to`. */
export interface Flight {
  readonly id: string;
  readonly from: Anchor;
  readonly to: Anchor;
  readonly at: number;
  readonly ms: number;
  /** The card it shows: absent, its back. */
  readonly face?: string;
  /** It turns over on the way: back to `face` («up»), or `face` to back («down»). */
  readonly turn?: "up" | "down";
  /** It stays where it lands until a scene lists it in `gone` or the moment ends: your dealt stack, a card turned up. */
  readonly stays?: boolean;
}

/** A card of your hand flown to its place in the fan: until `at` it is not there at all. */
export interface Arrival {
  readonly card: string;
  readonly from: Anchor;
  readonly at: number;
  readonly ms: number;
}

/** A banner in the row (and the mark it lands on a seat): an event, and who was on turn in its frame. */
export type ShownEffect = HaftkhabisEvent & { readonly turnAt: number };

/** A round that ended, as its card draws it. */
export interface RoundCardData extends Omit<HaftkhabisRoundResult, "hands"> {
  readonly seats: number;
  readonly targetScore: number;
  readonly matchOver: boolean;
  /** Per seat, how many cards it held when the round ended (the winner's 0). */
  readonly counts: readonly number[];
  /** Per seat, the cards themselves: every seat's, off the server's `lastRound.hands`. A frame without
   *  them (a frame in flight at a push) names yours alone, and a seat's `null` is then a count and nothing more. */
  readonly hands: readonly (readonly string[] | null)[];
}

/** What the table draws at one instant. */
export interface Scene {
  /** Every number and hand on screen. */
  readonly view: HaftkhabisView;
  /** The discard's top: `null` while the table is clear (the deal, before the first card is turned). */
  readonly top: string | null;
  /** The edge the top flies in from, when it is new here: `null`, it just lies there. */
  readonly topFrom: SeatEdge | null;
  /** The card the top lies on. */
  readonly under: string | null;
  /** A Jack's named suit, on show. */
  readonly suit: HaftkhabisView["activeSuit"] | null;
  /** The seat carrying the turn's glow: `null`, nobody's (the deal, a round's end). */
  readonly glow: number | null;
  /** The newest effect of this moment: its banner outlives the moment (`use-haftkhabis-feed.ts`). */
  readonly effect: ShownEffect | null;
  /** A 10's seq from the instant the direction flips: the mark's one flip. */
  readonly flip: number | null;
  /** Hand cards to fly in as this scene commits. */
  readonly arrivals: readonly Arrival[];
  /** Flights of this moment no longer drawn. */
  readonly gone: readonly string[];
  /** The round's card, and how far it has got: the remaining hands, then the table of points. */
  readonly round: { readonly card: RoundCardData; readonly stage: "hands" | "points" } | null;
  /** The take's panel is going: its card has been tapped and is on its way (only ever at its target). */
  readonly closingTake: boolean;
}

/** A sound, at its time on the moment's clock; `landing` is what it marks, for the record. */
export interface Cue {
  readonly at: number;
  readonly sfx: SfxName;
  readonly moment: number;
}

export type MomentKind =
  "deal" | "roundEnd" | "play" | "draw" | "pay" | "take" | "press" | "catch" | "nothing";

export interface Moment {
  /** Unique for the mount. */
  readonly id: string;
  readonly kind: MomentKind;
  /** The deal, a round's end: the server holds the first window for these, and that window ends them. */
  readonly opening: boolean;
  /** Each scene from `at` on; the first at 0. */
  readonly scenes: readonly { readonly at: number; readonly scene: Scene }[];
  readonly flights: readonly Flight[];
  readonly cues: readonly Cue[];
  /** Its span: the engine's own number. */
  readonly ms: number;
}

const EFFECT_KINDS: ReadonlySet<HaftkhabisEvent["kind"]> = new Set([
  "seven",
  "bonus",
  "skip",
  "reverse",
  "suit",
  "target",
]);

/** Where a seat is drawn, relative to you: حکم's diamond, seat+1 on your RIGHT. */
export const edgeOfSeat = (seat: number, you: number): SeatEdge =>
  seatEdge(DIAMOND_SEAT_EDGES, (seat - you + 4) % 4);

const withCount = (counts: readonly number[], seat: number, by: number): number[] =>
  counts.map((n, s) => (s === seat ? n + by : n));

/** The suit chip a settled table shows: a Jack that named one. A round's top is never a Jack (D1). */
function suitChip(view: HaftkhabisView): HaftkhabisView["activeSuit"] | null {
  return view.top.charAt(0) === "J" && view.phase !== "gameOver" ? view.activeSuit : null;
}

/** The table as a frame leaves it: what is drawn with no moment on screen. */
export function settledScene(view: HaftkhabisView, under: string | null): Scene {
  return {
    view,
    top: view.top,
    topFrom: null,
    under: view.discardCount > 0 ? under : null,
    suit: suitChip(view),
    glow: view.phase === "gameOver" ? null : view.turn,
    effect: null,
    flip: null,
    arrivals: [],
    gone: [],
    round: null,
    closingTake: false,
  };
}

/** A moment under construction: scenes pushed in time order, each the last one changed. */
class Plan {
  readonly scenes: { at: number; scene: Scene }[];
  readonly flights: Flight[] = [];
  readonly cues: Cue[] = [];

  constructor(first: Scene) {
    this.scenes = [{ at: 0, scene: first }];
  }

  get last(): Scene {
    return this.scenes[this.scenes.length - 1]!.scene;
  }

  show(at: number, patch: Partial<Scene> = {}, view: Partial<HaftkhabisView> = {}): void {
    const last = this.last;
    this.scenes.push({
      at,
      scene: {
        ...last,
        arrivals: [],
        ...patch,
        view: { ...last.view, ...view },
      },
    });
  }

  /** `clip` heard at `moment`. */
  cue(moment: number, clip: HaftkhabisClip): void {
    this.cues.push({ at: cueAt(moment, clip), sfx: clip, moment });
  }

  done(id: string, kind: MomentKind, ms: number, opening = false): Moment {
    return {
      id,
      kind,
      opening,
      scenes: this.scenes,
      flights: this.flights,
      cues: [...this.cues].sort((a, b) => a.at - b.at),
      ms,
    };
  }
}

const effectOfEvent = (event: HaftkhabisEvent, turnAt: number): ShownEffect => ({
  ...event,
  turnAt,
});

/** Your new cards off the frames: the event's `cards` reach only the seat that drew, so ask the hands. */
const newCards = (prev: HaftkhabisView, next: HaftkhabisView): string[] =>
  next.hand.filter((card) => !prev.hand.includes(card));

/**
 * Cards off the pile to a seat, one at a time from `start`: each leaves, and on reaching the seat both counts change
 * («a draw leaves the pile and reaches the seat with both counts changing at that moment») and the sound marks it.
 * Yours fly to their own places in the fan (`Arrival`, read off the plan's FIRST scene); anyone's else a back.
 */
function drawCards(
  plan: Plan,
  start: number,
  step: number,
  seat: number,
  count: number,
  keyOf: (i: number) => string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  paying = false,
): Arrival[] {
  const you = next.you;
  const mine = seat === you ? newCards(prev, next) : [];
  const arrivals: Arrival[] = [];
  for (let i = 0; i < count; i++) {
    const at = start + i * step;
    const land = at + DRAW_FLIGHT_MS;
    const card = mine[i];
    if (card !== undefined) arrivals.push({ card, from: "pile", at, ms: DRAW_FLIGHT_MS });
    else if (seat !== you)
      plan.flights.push({ id: keyOf(i), from: "pile", to: seat, at, ms: DRAW_FLIGHT_MS });
    plan.cue(land, "deal");
    const last = plan.last.view;
    plan.show(
      land,
      {},
      {
        handCounts: withCount(last.handCounts, seat, 1),
        pileCount: Math.max(0, last.pileCount - 1),
        ...(paying ? { penalty: Math.max(0, last.penalty - 1) } : {}),
      },
    );
  }
  return arrivals;
}

/** D3: the discards under the top become the pile, first; what follows draws from it. */
function reshuffleAt(
  plan: Plan,
  at: number,
  event: HaftkhabisEvent | undefined,
  next: HaftkhabisView,
  count: number,
): number {
  if (event === undefined) return at;
  const shows = at + CLIP_LEAD_MS.shuffle;
  plan.cue(shows, "shuffle");
  plan.show(
    shows,
    { effect: effectOfEvent(event, next.turn), under: null },
    { pileCount: next.pileCount + count, discardCount: next.discardCount },
  );
  return at + RESHUFFLE_MS;
}

function playMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  move: readonly HaftkhabisEvent[],
  base: Scene,
): Moment | null {
  const play = move.find((e) => e.kind === "play");
  if (play?.kind !== "play") return null;
  const you = next.you;
  const { seat, card } = play;
  const land = PLAY_FLIGHT_MS;
  // a 2 that makes you draw: the card has its place in your fan from the moment's start, as any draw of yours has
  const youDraw = move.some((e) => e.kind === "target" && e.via === "draw" && e.target === you);
  const plan = new Plan({
    ...base,
    view: {
      ...prev,
      hand: seat === you ? prev.hand.filter((c) => c !== card) : youDraw ? next.hand : prev.hand,
      drawnCard: null,
    },
    top: card,
    topFrom: edgeOfSeat(seat, you),
    under: prev.top,
    suit: null,
    glow: seat,
  });
  // THE CARD LANDS: its sound, the seat's count, the discard's; whoever now owes or is safe
  plan.cue(land, "play");
  const press = move.find((e) => e.kind === "press");
  // your own press sounded in your tap; another's at its bubble
  if (press?.kind === "press" && press.seat !== you) plan.cue(land, "bid");
  plan.show(land, press ? { effect: effectOfEvent(press, next.turn) } : {}, {
    handCounts: withCount(prev.handCounts, seat, -1),
    discardCount: prev.discardCount + 1,
    owes: next.owes,
    safe: next.safe,
  });
  // THEN ITS EFFECT
  const fx = move.find((e) => EFFECT_KINDS.has(e.kind));
  const at = playEffectAtMs(move);
  if (fx !== undefined) {
    const effect = effectOfEvent(fx, next.turn);
    switch (fx.kind) {
      case "seven":
        plan.cue(at, "qspade");
        plan.show(at, { effect }, { penalty: fx.penalty });
        break;
      case "bonus":
        plan.cue(at, "trickWin");
        plan.show(at, { effect });
        break;
      case "skip":
        plan.cue(at, "surCancel");
        plan.show(at, { effect, glow: fx.skipped }); // the turn passes over that seat, visibly
        break;
      case "reverse":
        plan.cue(at, "sur");
        plan.show(at, { effect, flip: fx.seq }, { direction: fx.direction });
        break;
      case "suit":
        plan.cue(at, "trumpCall");
        plan.show(at, { effect, suit: fx.suit }, { activeSuit: fx.suit });
        break;
      case "target": {
        plan.cue(at, "bid");
        plan.show(at, { effect, glow: fx.target });
        if (fx.via === "draw") {
          const drew = move.find((e) => e.kind === "draw");
          const count = drew?.kind === "draw" ? drew.count : 0;
          const reshuffled = reshuffleAt(
            plan,
            at + TARGET_BEAT_MS,
            move.find((e) => e.kind === "reshuffle"),
            next,
            count,
          );
          const arrivals = drawCards(
            plan,
            reshuffled,
            0,
            fx.target,
            count,
            () => `${id}:0`,
            prev,
            next,
          );
          plan.scenes[0] = { at: 0, scene: { ...plan.scenes[0]!.scene, arrivals } };
        }
        break;
      }
      default:
    }
  }
  return plan.done(id, "play", moveSpanMs(move));
}

function drawMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  move: readonly HaftkhabisEvent[],
  base: Scene,
): Moment | null {
  const head = move.find((e) => e.kind !== "reshuffle");
  if (head?.kind !== "draw" && head?.kind !== "penaltyPaid") return null;
  const paying = head.kind === "penaltyPaid";
  const seat = head.seat;
  const count = head.count;
  const mine = seat === next.you;
  const plan = new Plan({
    ...base,
    view: { ...prev, hand: mine ? next.hand : prev.hand, drawnCard: null },
    glow: seat,
    effect: paying || count === 0 ? effectOfEvent(head, next.turn) : null,
  });
  const start = reshuffleAt(
    plan,
    0,
    move.find((e) => e.kind === "reshuffle"),
    next,
    count,
  );
  const arrivals = drawCards(
    plan,
    start,
    paying ? PAY_STEP_MS : 0,
    seat,
    count,
    (i) => `${id}:${i}`,
    prev,
    next,
    paying,
  );
  // what a plain draw says it says when the card is there: an 8's owed draw, on landing; its press with it (D14)
  const landed = plan.scenes[plan.scenes.length - 1]!.at;
  if (!paying && count > 0 && head.reason === "bonus")
    plan.show(landed, { effect: effectOfEvent(head, next.turn) });
  const press = move.find((e) => e.kind === "press");
  if (press?.kind === "press") {
    if (press.seat !== next.you) plan.cue(landed, "bid");
    plan.show(
      landed,
      { effect: effectOfEvent(press, next.turn) },
      { owes: next.owes, safe: next.safe },
    );
  }
  plan.scenes[0] = { at: 0, scene: { ...plan.scenes[0]!.scene, arrivals } };
  return plan.done(id, count > 0 ? (paying ? "pay" : "draw") : "nothing", moveSpanMs(move));
}

function takeMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  move: readonly HaftkhabisEvent[],
  base: Scene,
): Moment | null {
  const take = move.find((e) => e.kind === "take");
  if (take?.kind !== "take") return null;
  const you = next.you;
  const { giver, target } = take;
  const yours = target === you;
  const plan = new Plan({
    ...base,
    view: {
      ...prev,
      pendingTake: null,
      hand: yours || giver === you ? next.hand : prev.hand,
    },
    glow: target,
    closingTake: yours,
    // at its target the card flies from the panel, to its place in your hand; a giver sees what went
    arrivals: yours
      ? newCards(prev, next).map((card) => ({ card, from: "take", at: 0, ms: TAKE_FLIGHT_MS }))
      : [],
  });
  if (!yours)
    plan.flights.push({
      id: `${id}:0`,
      from: giver,
      to: target,
      at: 0,
      ms: TAKE_FLIGHT_MS,
      ...(giver === you && take.card !== undefined ? { face: take.card } : {}),
    });
  plan.cue(TAKE_FLIGHT_MS, "pass");
  plan.show(
    TAKE_FLIGHT_MS,
    { effect: effectOfEvent(take, next.turn), closingTake: false },
    { handCounts: next.handCounts, owes: next.owes, safe: next.safe },
  );
  return plan.done(id, "take", moveSpanMs(move));
}

function pressMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  move: readonly HaftkhabisEvent[],
  base: Scene,
): Moment | null {
  const press = move.find((e) => e.kind === "press");
  if (press?.kind !== "press") return null;
  const plan = new Plan({ ...base, view: prev });
  if (press.seat !== next.you) plan.cue(PRESS_MS, "bid");
  plan.show(
    PRESS_MS,
    { effect: effectOfEvent(press, next.turn) },
    {
      owes: next.owes,
      safe: next.safe,
    },
  );
  return plan.done(id, "press", moveSpanMs(move));
}

function catchMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  move: readonly HaftkhabisEvent[],
  base: Scene,
): Moment | null {
  const caught = move.find((e) => e.kind === "catch");
  if (caught?.kind !== "catch") return null;
  const mine = caught.target === next.you;
  const plan = new Plan({
    ...base,
    view: { ...prev, hand: mine ? next.hand : prev.hand },
  });
  plan.cue(PRESS_MS, "heartsBroken");
  plan.show(
    PRESS_MS,
    { effect: effectOfEvent(caught, next.turn) },
    {
      owes: next.owes,
      safe: next.safe,
    },
  );
  if (caught.count > 0) {
    const start = reshuffleAt(
      plan,
      PRESS_MS + CATCH_BEAT_MS,
      move.find((e) => e.kind === "reshuffle"),
      next,
      caught.count,
    );
    const arrivals = drawCards(
      plan,
      start,
      0,
      caught.target,
      caught.count,
      () => `${id}:0`,
      prev,
      next,
    );
    plan.scenes[0] = { at: 0, scene: { ...plan.scenes[0]!.scene, arrivals } };
  }
  return plan.done(id, "catch", moveSpanMs(move));
}

/** The scene every moment starts from: the table as its frame found it. */
function baseScene(prev: HaftkhabisView, under: string | null): Scene {
  return { ...settledScene(prev, under), glow: prev.turn };
}

/**
 * A round's end: the winning card lands; a beat; every remaining hand; then the table of points. `prev` is the table the
 * last move found. The frame itself already holds the next deal, or the match's end.
 */
export function roundEndMoment(
  id: string,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  fresh: readonly HaftkhabisEvent[],
  end: Extract<HaftkhabisEvent, { kind: "roundEnd" }>,
  under: string | null,
): Moment {
  const you = next.you;
  const play = fresh.find((e) => e.kind === "play");
  const last = next.lastRound;
  // the table of who still held what: read off the round that ended, and only that round's
  const hands: readonly (readonly string[])[] | null =
    last?.round === end.round && Array.isArray(last.hands) ? last.hands : null;
  const counts = hands
    ? hands.map((hand) => hand.length)
    : prev.handCounts.map((n, seat) => (seat === end.winner ? 0 : n));
  const card: RoundCardData = {
    round: end.round,
    winner: end.winner,
    card: end.card,
    doubled: end.doubled,
    points: end.points,
    totalsAfter: end.totals,
    seats: next.seats,
    targetScore: next.targetScore,
    matchOver: end.matchOver,
    counts,
    hands: counts.map(
      (_n, seat) => hands?.[seat] ?? (seat === you ? (seat === end.winner ? [] : prev.hand) : null),
    ),
  };
  const ownHand = card.hands[you] ?? prev.hand;
  const plan = new Plan({
    ...baseScene(prev, under),
    view: {
      ...prev,
      hand: play?.kind === "play" && play.seat === you ? ownHand : prev.hand,
      drawnCard: null,
      pendingTake: null,
    },
    glow: play?.kind === "play" ? play.seat : null,
    ...(play?.kind === "play"
      ? { top: play.card, topFrom: edgeOfSeat(play.seat, you), under: prev.top, suit: null }
      : {}),
  });
  const landed = PLAY_FLIGHT_MS;
  if (play) plan.cue(landed, "play");
  plan.show(
    landed,
    { glow: null, suit: null },
    { handCounts: counts, hand: ownHand, penalty: 0, owes: [], safe: [] },
  );
  const handsAt = landed + ROUND_BEAT_MS;
  plan.cue(handsAt, "deal");
  plan.show(handsAt, { round: { card, stage: "hands" } });
  const pointsAt = handsAt + ROUND_HANDS_MS;
  plan.cue(pointsAt, end.winner === you ? "trickWin" : "trickLose");
  plan.show(pointsAt, { round: { card, stage: "points" } }, { totalScores: end.totals });
  return plan.done(id, "roundEnd", ROUND_END_SPAN_MS, true);
}

/** The event that opened this round: its D1 specials. */
const roundStartOf = (
  view: HaftkhabisView,
): Extract<HaftkhabisEvent, { kind: "roundStart" }> | null => {
  for (let i = view.events.length - 1; i >= 0; i--) {
    const event = view.events[i]!;
    if (event.kind === "roundStart") return event;
  }
  return null;
};

/**
 * A round's opening, `view` its first frame: THE TABLE IS CLEAR; the cards are dealt one at a time round the table from
 * the pile, starting after the dealer, seven each, each landing's count and sound; your stack fans; D1's specials turn up
 * and go back; only then the first card is turned face up onto the discard; a breath, and only then the turn shows.
 */
export function dealMoment(id: string, view: HaftkhabisView): Moment {
  const you = view.you;
  const seats = view.seats;
  const dealt = view.handCounts.reduce((sum, n) => sum + n, 0);
  const start = roundStartOf(view);
  const turnedBack = start?.turnedBack ?? [];
  const plan = new Plan({
    ...settledScene(view, null),
    view: {
      ...view,
      hand: [],
      handCounts: view.handCounts.map(() => 0),
      pileCount: view.pileCount + dealt + 1,
      discardCount: 0,
      penalty: 0,
      drawnCard: null,
      owes: [],
      safe: [],
    },
    top: null,
    glow: null,
    suit: null,
  });
  const stack: string[] = [];
  for (let k = 0; k < dealt; k++) {
    const seat = (view.dealer + 1 + k) % seats;
    const at = k * DEAL_STEP_MS;
    const land = at + DRAW_FLIGHT_MS;
    const flightId = `${id}:deal:${k}`;
    plan.flights.push(
      seat === you
        ? { id: flightId, from: "pile", to: "stack", at, ms: DRAW_FLIGHT_MS, stays: true }
        : { id: flightId, from: "pile", to: seat, at, ms: DRAW_FLIGHT_MS },
    );
    if (seat === you) stack.push(flightId);
    plan.cue(land, "deal");
    const last = plan.last.view;
    plan.show(
      land,
      {},
      {
        handCounts: withCount(last.handCounts, seat, 1),
        pileCount: last.pileCount - 1,
      },
    );
  }
  // THE HAND FANS
  plan.show(
    DEAL_LANDED_MS,
    {
      gone: stack,
      arrivals: view.hand.map((card) => ({
        card,
        from: "stack",
        at: DEAL_LANDED_MS,
        ms: FAN_MS,
      })),
    },
    { hand: view.hand },
  );
  // D1: a special turned up lies there, in front of everyone, and goes back into the pile
  let at = DEAL_LANDED_MS + FAN_MS;
  const shown = turnedBack.slice(-MAX_TURNED_BACK_SHOWN);
  for (const [i, card] of shown.entries()) {
    const up = `${id}:up:${i}`;
    plan.flights.push({
      id: up,
      from: "pile",
      to: "discard",
      at,
      ms: FLIP_MS,
      face: card,
      turn: "up",
      stays: true,
    });
    const lands = at + FLIP_MS;
    plan.cue(lands, "play");
    plan.show(lands, start ? { effect: effectOfEvent(start, view.turn) } : {}, {
      pileCount: plan.last.view.pileCount - 1,
    });
    const leaves = lands + TURNED_BACK_SHOW_MS;
    plan.flights.push({
      id: `${id}:back:${i}`,
      from: "discard",
      to: "pile",
      at: leaves,
      ms: TURNED_BACK_RETURN_MS,
      face: card,
      turn: "down",
    });
    plan.show(leaves, { gone: [...plan.last.gone, up] });
    const back = leaves + TURNED_BACK_RETURN_MS;
    plan.cue(back, "deal");
    plan.show(
      back,
      { gone: [...plan.last.gone, `${id}:back:${i}`] },
      { pileCount: plan.last.view.pileCount + 1 },
    );
    at = back;
  }
  // THE FIRST CARD, TURNED FACE UP ONTO THE DISCARD
  const first = `${id}:up`;
  plan.flights.push({
    id: first,
    from: "pile",
    to: "discard",
    at,
    ms: FLIP_MS,
    face: view.top,
    turn: "up",
    stays: true,
  });
  plan.cue(at + FLIP_MS, "play");
  plan.show(
    at + FLIP_MS,
    { top: view.top, suit: null, gone: [...plan.last.gone, first] },
    {
      pileCount: view.pileCount,
      discardCount: view.discardCount,
    },
  );
  return plan.done(id, "deal", openingSpanMs(turnedBack.length), true);
}

/** Is this event where a new move begins in a frame's fresh list? */
const startsMove = (event: HaftkhabisEvent): boolean =>
  event.kind === "play" ||
  event.kind === "pass" ||
  event.kind === "take" ||
  event.kind === "penaltyPaid" ||
  (event.kind === "draw" && event.reason !== "two") ||
  event.kind === "catch";

/** One frame carries one move, however many events it makes: more means frames were missed. */
export const movesIn = (fresh: readonly HaftkhabisEvent[]): number =>
  fresh.filter(startsMove).length +
  fresh.filter((e, i) => {
    const before = fresh[i - 1];
    return (
      e.kind === "press" &&
      !((before?.kind === "play" || before?.kind === "draw") && before.seat === e.seat)
    );
  }).length;

/**
 * THE MOMENTS ONE FRAME BRINGS over the frame before it: the move's, or (a round's last move: `play`, `roundEnd`,
 * `roundStart` in one frame) the round's end and the next deal. A kept card (`pass`) changes nothing to see.
 */
export function momentsOf(
  serial: number,
  prev: HaftkhabisView,
  next: HaftkhabisView,
  fresh: readonly HaftkhabisEvent[],
  under: string | null,
): Moment[] {
  const id = `${serial}:${fresh[0]?.seq ?? 0}`;
  const end = fresh.find((e) => e.kind === "roundEnd");
  if (end?.kind === "roundEnd") {
    const ended = roundEndMoment(`${id}:end`, prev, next, fresh, end, under);
    return next.phase === "gameOver" || end.matchOver
      ? [ended]
      : [ended, dealMoment(`${id}:next`, next)];
  }
  const base = baseScene(prev, under);
  const head = fresh.find((e) => e.kind !== "reshuffle");
  const moment =
    head?.kind === "play"
      ? playMoment(id, prev, next, fresh, base)
      : head?.kind === "draw" || head?.kind === "penaltyPaid"
        ? drawMoment(id, prev, next, fresh, base)
        : head?.kind === "take"
          ? takeMoment(id, prev, next, fresh, base)
          : head?.kind === "press"
            ? pressMoment(id, prev, next, fresh, base)
            : head?.kind === "catch"
              ? catchMoment(id, prev, next, fresh, base)
              : null;
  return moment === null ? [] : [moment];
}
